import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@components/ui/tooltip';
import type { WorkspaceVersion } from '@stratumn/studio-shared';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const LATEST_DESCRIPTION = 'The last version generated for this env';

const VERSION_TYPE_COLORS: Record<
  string,
  { bg: string; border: string; outline: string }
> = {
  tag: {
    bg: 'bg-emerald-600',
    border: 'border-emerald-700',
    outline: 'outline-emerald-700'
  },
  release: {
    bg: 'bg-blue-600',
    border: 'border-blue-700',
    outline: 'outline-blue-700'
  },
  feature: {
    bg: 'bg-violet-600',
    border: 'border-violet-700',
    outline: 'outline-violet-700'
  },
  hotfix: {
    bg: 'bg-orange-600',
    border: 'border-orange-700',
    outline: 'outline-orange-700'
  },
  superfix: {
    bg: 'bg-red-600',
    border: 'border-red-700',
    outline: 'outline-red-700'
  }
};

const DEFAULT_TYPE_COLOR = {
  bg: 'bg-gray-600',
  border: 'border-gray-700',
  outline: 'outline-gray-700'
};

const getTypeColor = (type: string) =>
  VERSION_TYPE_COLORS[type.toLowerCase()] ?? DEFAULT_TYPE_COLOR;

type ChevronProps = {
  chevron?: boolean;
  open?: boolean;
};

type Props = ChevronProps & {
  version: WorkspaceVersion | null;
};

const TypedPill = ({
  version,
  chevron,
  open
}: ChevronProps & { version: WorkspaceVersion }) => {
  const color = getTypeColor(version.type);
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-0 max-w-full items-center overflow-hidden rounded-full text-xs outline outline-1',
        color.outline
      )}
    >
      <span
        className={cn(
          'flex h-full shrink-0 items-center justify-center border-r pr-2 pl-3 text-background',
          color.bg,
          color.border
        )}
      >
        {version.type}
      </span>
      <span
        className={cn(
          'flex h-full min-w-0 items-center pl-2',
          chevron ? 'pr-1' : 'pr-3'
        )}
      >
        <span className="truncate">{version.displayName}</span>
      </span>
      {chevron && (
        <ChevronDown
          className={cn(
            'mr-1.5 h-3 w-3 shrink-0 opacity-50 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      )}
    </span>
  );
};

const LatestPill = ({ chevron, open }: ChevronProps) => (
  <span className="inline-flex h-5 min-w-0 max-w-full items-center overflow-hidden rounded-full bg-blue-100 text-blue-700 text-xs outline outline-1 outline-blue-200">
    <span
      className={cn('flex h-full items-center pl-3', chevron ? 'pr-1' : 'pr-3')}
    >
      Latest version
    </span>
    {chevron && (
      <ChevronDown
        className={cn(
          'mr-1.5 h-3 w-3 shrink-0 opacity-50 transition-transform duration-200',
          open && 'rotate-180'
        )}
      />
    )}
  </span>
);

export const VersionBadge = ({ version, chevron, open }: Props) => {
  const isLatest = version === null;
  const tooltipContent = isLatest ? LATEST_DESCRIPTION : version.name;
  const pill = isLatest ? (
    <LatestPill chevron={chevron} open={open} />
  ) : (
    <TypedPill chevron={chevron} open={open} version={version} />
  );

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        {/*
          Render the trigger as a focusable <button> so keyboard users can
          surface the tooltip (Radix's `asChild` does not auto-add tabindex
          to non-interactive elements). The button has no border/padding so
          it visually matches the original <span> wrapper.
        */}
        <TooltipTrigger asChild>
          <button
            aria-label={tooltipContent}
            className="inline-flex min-w-0 max-w-full items-center bg-transparent p-0 text-left"
            type="button"
          >
            {pill}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <span className={cn('text-xs', !isLatest && 'font-mono')}>
            {tooltipContent}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
